/**
 * MessageList - Scrollable message list with auto-scroll and load-more.
 */

import { useRef, useEffect, useCallback } from 'react'
import { useGroupChat } from '../GroupChatContext'
import { MessageBubble } from './MessageBubble'

export function MessageList() {
  const { messages, hasMoreMessages, loadMore } = useGroupChat()

  const containerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isNearBottomRef = useRef(true)
  const prevMessageCountRef = useRef(0)

  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 100

    if (scrollTop < 50 && hasMoreMessages) {
      loadMore()
    }
  }, [hasMoreMessages, loadMore])

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current && isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessageCountRef.current = messages.length
  }, [messages.length])

  useEffect(() => {
    if (messages.length > 0 && prevMessageCountRef.current === 0) {
      bottomRef.current?.scrollIntoView()
    }
  }, [messages.length])

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-4 py-3"
    >
      {hasMoreMessages && messages.length > 0 && (
        <div className="text-center py-2">
          <button
            onClick={() => loadMore()}
            className="text-pink-400 text-xs hover:text-pink-600 transition-colors"
          >
            Load older messages
          </button>
        </div>
      )}

      {messages.length === 0 && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-400 text-lg mb-2">No messages yet</p>
            <p className="text-gray-300 text-sm">Be the first to say something!</p>
          </div>
        </div>
      )}

      {messages.map((message, index) => {
        const prevMessage = index > 0 ? messages[index - 1] : null
        const showUsername = !prevMessage || prevMessage.user_id !== message.user_id
        const showTimestamp =
          !prevMessage ||
          new Date(message.created_at).getTime() -
            new Date(prevMessage.created_at).getTime() >
            300000

        return (
          <MessageBubble
            key={message.id}
            message={message}
            showUsername={showUsername || showTimestamp}
          />
        )
      })}

      <div ref={bottomRef} />
    </div>
  )
}
